import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Bot, MessageSquareText } from "lucide-react";
import ChatMessage from "./chat-message";
import ChatInput from "./chat-input";
import ChatFiltersBar from "./chat-filters";
import ChatSidebarSessions from "./chat-sidebar-sessions";
import {
  chatKeys,
  createChatSession,
  streamChatMessage,
  useChatMessages,
  useChatSessions,
  useDeleteChatSession,
  type ChatFilters,
  type ChatSourceOut,
} from "@/hooks/use-chat";
import { citationMarker } from "@/lib/chat-citations";
import { EmptyState } from "@/components/ui/empty-state";

interface UiMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  sources?: ChatSourceOut[];
  model?: string | null;
  created_at: string;
  /** id de la traza Langfuse (para el feedback 👍/👎). */
  traceId?: string | null;
  /** true hasta el primer delta → muestra "Pensando…". */
  pending?: boolean;
}

interface ChatInterfaceProps {
  projectId: string;
}

export default function ChatInterface({ projectId }: ChatInterfaceProps) {
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  const [activeSessionId, setActiveSessionId] = useState<string | undefined>();
  const [messages, setMessages] = useState<UiMessage[]>([]);
  // messageId → langfuse trace_id (para el 👍/👎). Mapa aparte porque el
  // histórico que llega del servidor no incluye el trace_id y pisaría el local.
  const [traceIds, setTraceIds] = useState<Record<string, string>>({});
  const [streaming, setStreaming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [filters, setFilters] = useState<ChatFilters>({});

  const sessionsQuery = useChatSessions(projectId);
  const sessions = sessionsQuery.data ?? [];
  const deleteSession = useDeleteChatSession(projectId);

  const messagesQuery = useChatMessages(activeSessionId);

  // Cargar histórico al cambiar de sesión (sin pisar un stream en curso).
  useEffect(() => {
    if (streaming) return;
    const data = messagesQuery.data;
    if (!data) return;
    setMessages((prev) => {
      // No pisar el resultado de un stream recién terminado con cache
      // viejo: solo sincronizamos si el servidor trae al menos tantos
      // mensajes como tenemos en local (tras el invalidate del onDone
      // llega el histórico completo y ahí sí sincroniza).
      if (data.length < prev.length) return prev;
      return data.map((m) => ({
        id: m.id,
        role: m.role,
        content: m.content,
        sources: m.sources ?? undefined,
        model: m.model ?? undefined,
        created_at: m.created_at,
      }));
    });
  }, [messagesQuery.data, streaming]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // ---- Streaming ----

  const updateAssistant = useCallback(
    (assistantId: string, updater: (msg: UiMessage) => UiMessage) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? updater(m) : m))
      );
    },
    []
  );

  const handleSend = useCallback(
    async (content: string) => {
      if (streaming) return;
      setErrorMessage(null);
      setStreaming(true);
      const controller = new AbortController();
      abortRef.current = controller;

      const now = new Date().toISOString();
      const userTempId = `temp-user-${Date.now()}`;
      const assistantTempId = `temp-assistant-${Date.now()}`;

      setMessages((prev) => [
        ...prev,
        { id: userTempId, role: "user", content, created_at: now },
        {
          id: assistantTempId,
          role: "assistant",
          content: "",
          sources: [],
          created_at: now,
          pending: true,
        },
      ]);

      let gotDelta = false;

      try {
        let sid = activeSessionId;
        if (!sid) {
          const session = await createChatSession(
            projectId,
            content.slice(0, 80)
          );
          sid = session.id;
          setActiveSessionId(sid);
          queryClient.invalidateQueries({
            queryKey: chatKeys.sessions(projectId),
          });
        }

        // Solo enviamos los grupos de filtros que tengan valor.
        const activeFilters: ChatFilters = {};
        if (filters.email_sender && filters.email_sender.trim())
          activeFilters.email_sender = filters.email_sender.trim();
        if (filters.date_from) activeFilters.date_from = filters.date_from;
        if (filters.date_to) activeFilters.date_to = filters.date_to;
        if (filters.document_ids && filters.document_ids.length > 0)
          activeFilters.document_ids = filters.document_ids;
        const hasFilters = Object.keys(activeFilters).length > 0;

        await streamChatMessage(
          sid,
          { content, filters: hasFilters ? activeFilters : undefined },
          {
            onSources: (sources) => {
              updateAssistant(assistantTempId, (m) => ({ ...m, sources }));
            },
            onDelta: (text) => {
              gotDelta = true;
              updateAssistant(assistantTempId, (m) => ({
                ...m,
                pending: false,
                content: m.content + text,
              }));
            },
            onCitation: (citation) => {
              // El chip se ancla a la posición N de la lista de fuentes;
              // insertamos el marcador [[cita:N]] en el punto del texto en
              // el que llegó el evento.
              updateAssistant(assistantTempId, (m) => {
                const sources = [...(m.sources ?? [])];
                let idx = sources.findIndex(
                  (s) => s.chunk_id === citation.chunk_id
                );
                if (idx === -1) {
                  sources.push({
                    chunk_id: citation.chunk_id,
                    document_id: citation.document_id,
                    filename: citation.filename,
                    page: citation.page,
                    cited_text: citation.cited_text,
                  });
                  idx = sources.length - 1;
                } else {
                  sources[idx] = {
                    ...sources[idx],
                    cited_text:
                      citation.cited_text ?? sources[idx].cited_text,
                  };
                }
                return {
                  ...m,
                  sources,
                  content: m.content + citationMarker(idx + 1),
                };
              });
            },
            onDone: (done) => {
              updateAssistant(assistantTempId, (m) => ({
                ...m,
                id: done.message_id || m.id,
                pending: false,
                model: done.model ?? m.model,
                traceId: done.langfuse_trace_id ?? m.traceId,
              }));
              if (done.langfuse_trace_id && done.message_id) {
                setTraceIds((prev) => ({
                  ...prev,
                  [done.message_id]: done.langfuse_trace_id as string,
                }));
              }
              updateAssistant(assistantTempId, (m) => ({
                ...m,
                // Conservamos el contenido local (lleva los marcadores de
                // cita posicionados); el del evento es el fallback.
                content: m.content || done.content,
              }));
              queryClient.invalidateQueries({
                queryKey: chatKeys.messages(sid!),
              });
              queryClient.invalidateQueries({
                queryKey: chatKeys.sessions(projectId),
              });
            },
          },
          controller.signal
        );
      } catch (error) {
        if (controller.signal.aborted) {
          // Cancelado por el usuario: conservamos lo recibido, sin error.
          updateAssistant(assistantTempId, (m) => ({ ...m, pending: false }));
        } else {
          const msg =
            error instanceof Error
              ? error.message
              : "Error al enviar el mensaje. Inténtalo de nuevo.";
          setErrorMessage(msg);
          if (!gotDelta) {
            // Falló antes de empezar la respuesta: quitamos los optimistas.
            setMessages((prev) =>
              prev.filter(
                (m) => m.id !== userTempId && m.id !== assistantTempId
              )
            );
          } else {
            updateAssistant(assistantTempId, (m) => ({ ...m, pending: false }));
          }
        }
      } finally {
        setStreaming(false);
        abortRef.current = null;
      }
    },
    [streaming, activeSessionId, projectId, queryClient, updateAssistant, filters]
  );

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const handleNewSession = useCallback(() => {
    if (streaming) return;
    setActiveSessionId(undefined);
    setMessages([]);
    setErrorMessage(null);
  }, [streaming]);

  const handleSelectSession = useCallback(
    (id: string) => {
      if (streaming) return;
      setActiveSessionId(id);
      setMessages([]);
      setErrorMessage(null);
    },
    [streaming]
  );

  const handleDeleteSession = useCallback(
    (id: string) => {
      deleteSession.mutate(id);
      if (id === activeSessionId) {
        setActiveSessionId(undefined);
        setMessages([]);
      }
    },
    [deleteSession, activeSessionId]
  );

  const showEmptyState = messages.length === 0 && !streaming;

  return (
    <div className="flex h-full bg-surface rounded-lg shadow-card overflow-hidden">
      <ChatSidebarSessions
        sessions={sessions}
        activeSessionId={activeSessionId}
        onSelectSession={handleSelectSession}
        onNewSession={handleNewSession}
        onDeleteSession={handleDeleteSession}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-line bg-surface">
          <h2 className="text-lg font-semibold text-ink">
            Chat con los documentos
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {showEmptyState ? (
            <EmptyState
              icon={<MessageSquareText />}
              title="Pregunta a tus documentos"
              description="Las respuestas incluyen citas a los documentos del proyecto."
            />
          ) : (
            <>
              {messages.map((msg) =>
                msg.pending && !msg.content ? (
                  // Indicador "Pensando…" hasta el primer delta.
                  <div key={msg.id} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-surface border border-line rounded px-4 py-3 flex items-center gap-2">
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 bg-brand rounded-full animate-bounce" />
                        <span
                          className="w-1.5 h-1.5 bg-brand rounded-full animate-bounce"
                          style={{ animationDelay: "0.15s" }}
                        />
                        <span
                          className="w-1.5 h-1.5 bg-brand rounded-full animate-bounce"
                          style={{ animationDelay: "0.3s" }}
                        />
                      </span>
                      <p className="text-sm text-content-muted">Pensando…</p>
                    </div>
                  </div>
                ) : (
                  <ChatMessage
                    key={msg.id}
                    id={msg.id}
                    role={msg.role}
                    content={msg.content}
                    timestamp={new Date(msg.created_at)}
                    sources={msg.sources}
                    model={msg.model}
                    traceId={msg.traceId ?? traceIds[msg.id]}
                  />
                )
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {errorMessage && (
          <div
            className="mx-4 mb-2 p-3 bg-danger/10 border border-danger/30 rounded flex items-center gap-2 text-sm text-danger"
            role="alert"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">{errorMessage}</span>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-danger hover:text-danger/70"
              aria-label="Cerrar error"
            >
              &times;
            </button>
          </div>
        )}

        <ChatFiltersBar
          projectId={projectId}
          filters={filters}
          onChange={setFilters}
          disabled={streaming}
        />

        <ChatInput
          onSend={handleSend}
          onStop={handleStop}
          loading={streaming}
          placeholder="Pregunta sobre los documentos del proyecto..."
        />
      </div>
    </div>
  );
}
